import { CHART_SERIES_PALETTE } from "@/shared/ui/chart/public";
import {
  formatDetailNumber,
  isSuccessOutcome,
  outcomeBreakdownFromEpisodes,
} from "../evalRunResultModel";
import {
  MERGED_SERIES_KEY,
  joinDistinct,
  scalarCompareDistributions,
  type CompareRun,
  type EvalResultSeries,
} from "./evalCompareModel";
import { summaryStatGroups } from "./evalCompareSummaryModel";

export type EvalMergeExclusion =
  | { kind: "duplicate_seed"; runId: string; seed: string; retainedRunId: string }
  | { kind: "missing_seed"; runId: string }
  | { kind: "terms_unavailable"; runId: string };

type SeedAggregateStats = {
  count: number;
  mean: number | null;
  sampleSd: number | null;
  min: number | null;
  max: number | null;
};

export type SeedStabilityRow = {
  key: string;
  group: string;
  statistic: string;
  stats: SeedAggregateStats;
  format: (value: number) => string;
  formatSpread: (value: number) => string;
};

export type EvalMergedResult = {
  series: EvalResultSeries;
  sourceSeries: EvalResultSeries[];
  // 집계에 실제로 들어간 run 이다. 제외 목록에서 역산하면 같은 판정이 두 곳에 생긴다.
  includedItems: CompareRun[];
  selectedRunCount: number;
  includedRunCount: number;
  uniqueSeedCount: number;
  episodeCount: number;
  episodesPerSeed: { min: number; max: number } | null;
  exclusions: EvalMergeExclusion[];
  stabilityRows: SeedStabilityRow[];
};

function episodeSuccessRate(item: CompareRun): number | null {
  const episodes = item.run.terms?.episodes ?? [];
  if (episodes.length === 0) return null;
  return episodes.filter((episode) => isSuccessOutcome(episode.outcome)).length / episodes.length;
}

function mergeSourceSeries(item: CompareRun): EvalResultSeries {
  return {
    key: item.key,
    summary: { ...item.summary, finalSuccessRate: episodeSuccessRate(item) },
    color: item.color,
    label: item.label,
    shortLabel: item.shortLabel,
    sublabel: item.sublabel,
    config: item.config,
  };
}

function aggregateSeedValues(values: readonly number[]): SeedAggregateStats {
  if (values.length === 0) {
    return { count: 0, mean: null, sampleSd: null, min: null, max: null };
  }
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const sampleSd =
    values.length > 1
      ? Math.sqrt(values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / (values.length - 1))
      : null;
  return {
    count: values.length,
    mean,
    sampleSd,
    min: Math.min(...values),
    max: Math.max(...values),
  };
}

function percentagePointFormat(multiplier: number): (value: number) => string {
  return (value) => `${formatDetailNumber(value * multiplier)} pp`;
}

function seedStabilityRows(items: readonly EvalResultSeries[]): SeedStabilityRow[] {
  return summaryStatGroups(items)
    .flatMap((group) =>
      group.columns.map((column) => {
        const values = items.map(column.value).filter((value): value is number => value !== null);
        const percentScale =
          column.key === "success" ? 100 : column.key.endsWith(":out") ? 1 : null;
        return {
          key: column.key,
          group: group.header ?? column.label,
          statistic: group.header === null ? "rate" : column.label,
          stats: aggregateSeedValues(values),
          format: column.format,
          formatSpread: percentScale === null ? column.format : percentagePointFormat(percentScale),
        };
      }),
    )
    .filter((row) => row.stats.count > 0);
}

export function buildMergedResult(items: readonly CompareRun[]): EvalMergedResult {
  const sourceSeries = items.map(mergeSourceSeries);
  const includedItems: CompareRun[] = [];
  const includedSeries: EvalResultSeries[] = [];
  const exclusions: EvalMergeExclusion[] = [];
  const retainedRunIdBySeed = new Map<string, string>();
  for (const [index, item] of items.entries()) {
    if (item.run.terms === null) {
      exclusions.push({ kind: "terms_unavailable", runId: item.run.id });
      continue;
    }
    const seed = item.simulationSeed;
    if (seed === null) {
      exclusions.push({ kind: "missing_seed", runId: item.run.id });
      continue;
    }
    const retainedRunId = retainedRunIdBySeed.get(seed);
    if (retainedRunId !== undefined) {
      exclusions.push({ kind: "duplicate_seed", runId: item.run.id, seed, retainedRunId });
      continue;
    }
    retainedRunIdBySeed.set(seed, item.run.id);
    includedItems.push(item);
    includedSeries.push(sourceSeries[index]);
  }

  const termViews = includedItems.map((item) => item.run.terms);
  const episodes = termViews.flatMap((terms) => terms?.episodes ?? []);
  const successCount = episodes.filter((episode) => isSuccessOutcome(episode.outcome)).length;
  const episodeCounts = termViews.map((terms) => terms?.episodes.length ?? 0);
  const episodeCount = episodes.length;
  const uniqueSeedCount = includedItems.length;
  return {
    sourceSeries,
    includedItems,
    selectedRunCount: items.length,
    includedRunCount: includedItems.length,
    uniqueSeedCount,
    episodeCount,
    episodesPerSeed:
      episodeCounts.length === 0
        ? null
        : { min: Math.min(...episodeCounts), max: Math.max(...episodeCounts) },
    exclusions,
    stabilityRows: seedStabilityRows(includedSeries),
    series: {
      key: MERGED_SERIES_KEY,
      summary: {
        finalSuccessRate: episodeCount > 0 ? successCount / episodeCount : null,
        durationSec: null,
        outcomeBreakdown: outcomeBreakdownFromEpisodes(episodes),
        distributions: scalarCompareDistributions(termViews),
      },
      color: CHART_SERIES_PALETTE[0],
      label: "통합 결과",
      shortLabel: "통합 결과",
      sublabel: `Seed ${uniqueSeedCount}개 · Episode ${episodeCount}개`,
      config: joinDistinct(includedItems.map((item) => item.config)),
    },
  };
}
